import React, { useState } from 'react';
import { useSelector, RootStateOrAny } from 'react-redux';
import ReactSVG from 'react-svg';
import { useControlSessions } from '~/hooks';
import { useTranslation } from '~/dependencies';
import AgentsList, { Agent, AgentActionsProps } from '~/components/AgentsList';
import { isValidInviteTarget } from '~/helpers';
import { IconTransfer } from '~/assets/svgIcons';

import { Container, ConferenceSearchNumber, ActionsContainer } from './styles';

type Props = {
  sessionId: string;
};

/**
 * Manage the conferenceNumber and perform call to conference party
 * @param {String} sessionId - A unformatted sessionId contains special characters symbols
 */
export default function ConferenceNumber({ sessionId }: Props) {
  const { t } = useTranslation();

  const [numberTo, setNumberTo] = useState<string>('');

  function onConferenceNumberChange(number: string) {
    setNumberTo(number);
  }

  return (
    <Container>
      <ConferenceSearchNumber qty={numberTo ? numberTo.length : 0}>
        <input
          value={numberTo}
          placeholder={t('components.activeCalls.conferenceNumber.placeholder')}
          onChange={event => onConferenceNumberChange(event.target.value)}
        />

        {isValidInviteTarget(numberTo) && (
          <ActionsContainer>
            <ConferenceActions sessionId={sessionId} numberTo={numberTo} />
          </ActionsContainer>
        )}
      </ConferenceSearchNumber>

      <AgentsList maxHeight="435" searchFor={numberTo}>
        {(agentActionsProps: AgentActionsProps) => (
          <ActionsContainer>
            <ConferenceActions {...agentActionsProps} sessionId={sessionId} />
          </ActionsContainer>
        )}
      </AgentsList>
    </Container>
  );
}

type ConferenceActionsProps = {
  sessionId: string;
  numberTo?: string;
  agent?: Agent;
};

const ConferenceActions = ({
  agent,
  sessionId,
  numberTo,
}: ConferenceActionsProps) => {
  const { t } = useTranslation();
  const controlSessions = useControlSessions();

  const activeSession = useSelector(
    (state: RootStateOrAny) => state.callCurrent?.session
  );

  const phoneNumber = agent?.number || numberTo;

  function callConferenceParty() {
    // Hold the current active call
    if (activeSession) controlSessions(activeSession.id, 'hold');

    controlSessions(sessionId, 'invite', phoneNumber, {
      waitingToMergeConf: true,
    });

    controlSessions(sessionId, 'conferenceOff');
  }

  return (
    <ActionsContainer>
      <button type="button" onClick={() => callConferenceParty()}>
        <ReactSVG src={IconTransfer} className="icon-conference" />
        <span> {t('components.activeCalls.conferenceNumber.callFirst')}</span>
      </button>
    </ActionsContainer>
  );
};

ConferenceActions.defaultProps = {
  numberTo: null,
  agent: null,
};
